import { useMemo, useRef, useState } from 'react';
import Icon from '../../components/common/Icon';
import { formatAreaDistance, type TourArea, type TourStation, type Vec3 } from '../../utils/tourAreas';
import type { TourMap as TourMapModel, TourMapNode } from '../../utils/tourMap';
import styles from './TourMap.module.css';

// Concept 4 of the Advanced Virtual Tour: the tour as a small 3D diagram.
// Drag turns it; Plan looks straight down. Picking a node shows what it
// holds, and Go here opens its nearest scan.

interface Props {
  map: TourMapModel;
  areas: TourArea[];
  currentStationId: string;
  // World heading the panorama faces (radians, atan2 of the scene's x/y).
  heading: number;
  photoOf: (stationId: string) => string | undefined;
  onGo: (stationId: string) => void;
  onClose: () => void;
}

const W = 600;
const H = 400;
const PAD = 40;
const ELEVATION = (32 * Math.PI) / 180;

export interface TourMapView { yaw: number; plan: boolean }

// Screen position of a map point: turned by yaw about the vertical, then seen
// from ELEVATION above the horizon (straight down in plan). Heights are drawn
// true: the map model already stacks storeys a readable gap apart.
export function projector(view: TourMapView) {
  const el = view.plan ? Math.PI / 2 : ELEVATION;
  const c = Math.cos(view.yaw), s = Math.sin(view.yaw);
  return (p: Vec3) => {
    const x = p.x * c - p.y * s;
    const y = p.x * s + p.y * c;
    return { x, y: -(y * Math.sin(el) + p.z * Math.cos(el)), depth: y * Math.cos(el) - p.z * Math.sin(el) };
  };
}

const radiusOf = (n: TourMapNode) => Math.min(13, 4.5 + 2.2 * Math.sqrt(n.stationIds.length - 1));

const TourMap = ({ map, areas, currentStationId, heading, photoOf, onGo, onClose }: Props) => {
  const [view, setView] = useState<TourMapView>({ yaw: -Math.PI / 5, plan: false });
  const [picked, setPicked] = useState<string | null>(null);
  const drag = useRef<{ x: number; moved: number } | null>(null);
  const areaOf = useMemo(() => new Map(areas.map((a) => [a.key, a])), [areas]);
  const nodeById = useMemo(() => new Map(map.nodes.map((n) => [n.id, n])), [map]);
  const here = map.nodeOf.get(currentStationId);
  const hereNode = here ? nodeById.get(here) : undefined;
  const herePos = areas.flatMap((a) => a.stations).find((s) => s.id === currentStationId)?.position;

  const layout = useMemo(() => {
    const raw = projector(view);
    const pts = [
      ...map.nodes.map((n) => raw(n.position)),
      ...map.plates.flatMap((p) => p.outline.map(([x, y]) => raw({ x, y, z: p.z }))),
    ];
    let a = Infinity, b = -Infinity, c = Infinity, d = -Infinity;
    for (const p of pts) { a = Math.min(a, p.x); b = Math.max(b, p.x); c = Math.min(c, p.y); d = Math.max(d, p.y); }
    const scale = Math.min((W - 2 * PAD) / Math.max(b - a, 1e-6), (H - 2 * PAD - 24) / Math.max(d - c, 1e-6));
    const ox = W / 2 - ((a + b) / 2) * scale;
    const oy = (H - 24) / 2 - ((c + d) / 2) * scale + 6;
    const project = (p: Vec3) => {
      const q = raw(p);
      return { x: ox + q.x * scale, y: oy + q.y * scale, depth: q.depth };
    };
    return { project, scale };
  }, [map, view]);

  const { project } = layout;
  const pos = (id: string) => project(nodeById.get(id)!.position);
  const plates = [...map.plates]
    .map((p) => ({ p, pts: p.outline.map(([x, y]) => project({ x, y, z: p.z })) }))
    .sort((u, v) => u.p.z - v.p.z || Math.min(...v.pts.map((q) => q.depth)) - Math.min(...u.pts.map((q) => q.depth)));
  const nodes = [...map.nodes].map((n) => ({ n, at: project(n.position) })).sort((u, v) => v.at.depth - u.at.depth);
  const pickedNode = picked ? nodeById.get(picked) : undefined;
  const pickedAt = pickedNode ? project(pickedNode.position) : null;
  const pickedArea = pickedNode ? areaOf.get(pickedNode.areaKey) : undefined;
  // The scan "Go here" opens: the one nearest the node's middle, where the dot
  // is drawn — not the one nearest you, which for a group up a minaret's stairs
  // was the bottom step under a dot drawn halfway up. The card describes it.
  const target = pickedNode ? middleScan(pickedNode, areas) : undefined;
  const pickedDistance = target && herePos ? Math.hypot(target.position.x - herePos.x, target.position.y - herePos.y) : 0;
  // A metre or more up or down is said: the plan distance alone hides a climb.
  const pickedRise = target && herePos ? target.position.z - herePos.z : 0;
  const riseText = Math.abs(pickedRise) >= 1 ? `, ${formatAreaDistance(Math.abs(pickedRise))} ${pickedRise > 0 ? 'higher' : 'lower'}` : '';

  const labelOf = (areaKey: string) => {
    const own = plates.filter((pl) => pl.p.areaKey === areaKey);
    const pts = own.flatMap((pl) => pl.pts);
    if (!pts.length) return null;
    const top = pts.reduce((m, q) => (q.y < m.y ? q : m), pts[0]);
    return { x: top.x, y: top.y - 8 };
  };

  const cone = (() => {
    if (!hereNode) return null;
    const r = 7;
    const at = hereNode.position;
    const tip = project(at);
    const side = (t: number) => project({ x: at.x + Math.cos(t) * r, y: at.y + Math.sin(t) * r, z: at.z });
    const p1 = side(heading - 0.45), p2 = side(heading + 0.45);
    return `M${tip.x},${tip.y} L${p1.x},${p1.y} L${p2.x},${p2.y} Z`;
  })();

  const onPointerDown = (e: React.PointerEvent) => {
    if ((e.target as Element).closest('[data-node]')) return;
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    drag.current = { x: e.clientX, moved: 0 };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    d.x = e.clientX;
    d.moved += Math.abs(dx);
    setView((v) => ({ ...v, yaw: v.yaw + dx * 0.012 }));
  };
  const onPointerUp = () => {
    if (drag.current && drag.current.moved < 3) setPicked(null);
    drag.current = null;
  };

  return (
    <section className={styles.panel} aria-label="Tour map">
      <header className={styles.head}>
        <span className={styles.title}>Tour map</span>
        <span className={styles.hint}>Drag to turn</span>
        <div className={styles.modes} role="group" aria-label="View">
          <button type="button" className={!view.plan ? styles.modeOn : styles.mode} aria-pressed={!view.plan} onClick={() => setView((v) => ({ ...v, plan: false }))}>3D</button>
          <button type="button" className={view.plan ? styles.modeOn : styles.mode} aria-pressed={view.plan} onClick={() => setView((v) => ({ ...v, plan: true }))}>Plan</button>
        </div>
        <button type="button" className={styles.close} onClick={onClose} aria-label="Close the tour map">
          <Icon name="close" />
        </button>
      </header>
      <div className={styles.stage}>
        <svg
          className={styles.svg}
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="xMidYMid meet"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {plates.map(({ p, pts }) => {
            const colour = areaOf.get(p.areaKey)?.colour ?? '#64748b';
            return (
              <polygon
                key={`${p.areaKey}-${p.level}`}
                points={pts.map((q) => `${q.x},${q.y}`).join(' ')}
                fill={colour}
                fillOpacity={0.13}
                stroke={colour}
                strokeOpacity={0.45}
                strokeWidth={1.2}
              />
            );
          })}
          {map.hops.map((h) => {
            const a = pos(h.a), b = pos(h.b);
            const colour = areaOf.get(nodeById.get(h.a)!.areaKey)?.colour ?? '#64748b';
            return <line key={`${h.a}-${h.b}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={colour} strokeOpacity={0.7} strokeWidth={1.6} />;
          })}
          {map.jumps.map((j) => {
            const a = pos(j.a), b = pos(j.b);
            const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
            const text = formatAreaDistance(j.distance);
            return (
              <g key={`${j.a}-${j.b}`}>
                <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#4b5569" strokeWidth={1.6} strokeDasharray="5 4" />
                <rect x={mx - text.length * 3.6 - 6} y={my - 10} width={text.length * 7.2 + 12} height={19} rx={9.5} className={styles.jumpPill} />
                <text x={mx} y={my + 4} className={styles.jumpText}>{text}</text>
              </g>
            );
          })}
          {cone && <path d={cone} className={styles.cone} />}
          {nodes.map(({ n, at }) => {
            const colour = areaOf.get(n.areaKey)?.colour ?? '#64748b';
            const r = radiusOf(n);
            const count = n.stationIds.length;
            const area = areaOf.get(n.areaKey);
            const isHere = n.id === here;
            return (
              <g
                key={n.id}
                data-node={n.id}
                role="button"
                tabIndex={0}
                aria-label={`${area?.name ?? 'Area'}, ${count} ${count === 1 ? 'scan' : 'scans'}${isHere ? ', you are here' : ''}`}
                className={styles.node}
                onClick={() => setPicked(n.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPicked(n.id); } }}
              >
                {isHere && <circle cx={at.x} cy={at.y} r={r + 6} className={styles.hereRing} />}
                <circle cx={at.x} cy={at.y} r={r} fill={colour} stroke={picked === n.id ? '#131b2e' : '#fff'} strokeWidth={picked === n.id ? 2.4 : 1.6} />
                {count > 1 && r >= 7.5 && <text x={at.x} y={at.y + 3.6} className={styles.count}>{count}</text>}
              </g>
            );
          })}
          {areas.map((a) => {
            const at = labelOf(a.key);
            if (!at) return null;
            return <text key={a.key} x={at.x} y={at.y} className={styles.areaLabel} fill={a.colour}>{a.name}</text>;
          })}
          {hereNode && (() => {
            const at = project(hereNode.position);
            return (
              <g className={styles.you} aria-hidden="true">
                <rect x={at.x - 46} y={at.y - 9} width={30} height={18} rx={4} />
                <text x={at.x - 31} y={at.y + 4}>You</text>
              </g>
            );
          })()}
        </svg>
        {pickedNode && pickedAt && pickedArea && (() => {
          const photo = target ? photoOf(target.id) : undefined;
          const count = pickedNode.stationIds.length;
          const left = Math.min(Math.max((pickedAt.x / W) * 100, 22), 78);
          const below = pickedAt.y / H < 0.5;
          return (
            <div
              className={`${styles.card} ${below ? styles.cardBelow : styles.cardAbove}`}
              style={{ left: `${left}%`, top: `${(pickedAt.y / H) * 100}%` }}
              role="dialog"
              aria-label={pickedArea.name}
            >
              {photo && <img className={styles.photo} src={photo} alt="" loading="lazy" crossOrigin="anonymous" draggable={false} />}
              <span className={styles.cardName}><span className={styles.dot} style={{ background: pickedArea.colour }} />{pickedArea.name}{pickedNode.level > 0 ? `, floor ${pickedNode.level + 1}` : ''}</span>
              <span className={styles.cardMeta}>
                {count} {count === 1 ? 'scan' : 'scans'} here{pickedNode.id === here ? ', you are here' : `, ${formatAreaDistance(pickedDistance)} away${riseText}`}
              </span>
              {pickedNode.id !== here && (
                <button type="button" className={styles.go} onClick={() => { if (target) onGo(target.id); }}>Go here</button>
              )}
            </div>
          );
        })()}
      </div>
      <footer className={styles.legend}>
        <span><span className={styles.legendDot} />Scans close together</span>
        <span><span className={styles.legendLine} />Ring hop</span>
        <span><span className={styles.legendDash} />Jump to another area</span>
      </footer>
    </section>
  );
};

export function middleScan(node: TourMapNode, areas: TourArea[]): TourStation | undefined {
  const ids = new Set(node.stationIds);
  const c = node.real;
  let best: TourStation | undefined, bestD = Infinity;
  for (const a of areas) for (const s of a.stations) {
    if (!ids.has(s.id)) continue;
    const d = Math.hypot(s.position.x - c.x, s.position.y - c.y, s.position.z - c.z);
    if (d < bestD) { bestD = d; best = s; }
  }
  return best;
}

export default TourMap;
